import React, { createContext, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import debounce from 'lodash/debounce';

import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import { useNativeCollabState } from '@confluence/native-collab';
import { fg } from '@confluence/feature-gating';

export type SaveIndicatorContextType = {
	isSaving: boolean;
	processNcsSaving: (autoSuccess?: boolean) => void;
	savedTime: Date;
};

export const SaveIndicatorContext = createContext<SaveIndicatorContextType>({
	processNcsSaving: () => {},
	isSaving: false,
	savedTime: new Date(),
});

type CommitStatusPayload = {
	version: number;
	status: 'attempt' | 'success' | 'failure';
};

const DEBOUNCE_SAVE_PERIOD = 1200;
const COMMIT_STATUS_DEBOUNCE = 500;
const NCS_POLLING_INTERVAL = 500;

type SaveIndicatorContextProviderProps = {
	editorApi?: PublicPluginAPI<[CollabEditPlugin]> | null;
	children: React.ReactNode;
};

export const SaveIndicatorContextProvider = ({
	editorApi,
	children,
}: SaveIndicatorContextProviderProps) => {
	const [isSaving, setIsSaving] = useState(false);
	const [savedTime, setSavedTime] = useState<Date>(new Date());
	const saveAttemptCallbackId = useRef<number>(0);
	const saveCompleteCallbackId = useRef<number>(0);
	const pollIntervalId = useRef<any>(0);

	const [{ collabEditProvider }] = useNativeCollabState();
	const { collabEditState } = useSharedPluginState(editorApi, ['collabEdit']);
	const lastOrganicBodyChangeAt = collabEditState?.initialised?.lastLocalOrganicBodyChangeAt;

	const useEditorBodyChangeEvent = fg('save_indicator_exclude_ghost_steps');

	// These window functions are used here as performance efficient callback throttlers
	// requestIdleCallback is not supported by all browsers, hence fallback to requestAnimationFrame
	const throttledCallback = window.requestIdleCallback || window.requestAnimationFrame;

	const cancelThrottle = useCallback(
		(id: number) => {
			if (throttledCallback === window.requestIdleCallback) {
				window.cancelIdleCallback(id);
			} else {
				window.cancelAnimationFrame(id);
			}
		},
		[throttledCallback],
	);

	const onSaveSuccess = useCallback(() => {
		if (saveCompleteCallbackId.current) {
			cancelThrottle(saveCompleteCallbackId.current);
		}
		const id = throttledCallback(() => {
			setSavedTime(new Date());
			setIsSaving(false);
			clearInterval(pollIntervalId.current);
			pollIntervalId.current = 0;
			saveCompleteCallbackId.current = 0;
		});
		saveCompleteCallbackId.current = id;
	}, [cancelThrottle, throttledCallback]);

	const onSaveAttempt = useCallback(
		(autoSuccess: boolean = false) => {
			let autoSuccessTimeoutId;
			if (!isSaving) {
				if (saveAttemptCallbackId.current) {
					cancelThrottle(saveAttemptCallbackId.current);
				}
				const attemptId = throttledCallback(() => {
					setIsSaving(true);
					saveAttemptCallbackId.current = 0;
					if (autoSuccess) {
						autoSuccessTimeoutId = setTimeout(onSaveSuccess, DEBOUNCE_SAVE_PERIOD);
					}
				});
				saveAttemptCallbackId.current = attemptId;
			}

			return () => {
				if (autoSuccessTimeoutId) {
					clearTimeout(autoSuccessTimeoutId);
				}
			};
		},
		[isSaving, cancelThrottle, throttledCallback, onSaveSuccess],
	);

	const poll = useCallback(() => {
		if (!collabEditProvider) {
			return;
		}

		const unconfirmedSteps = collabEditProvider.getUnconfirmedSteps();
		if (unconfirmedSteps && unconfirmedSteps.length > 0) {
			onSaveAttempt();
		} else {
			onSaveSuccess();
		}
	}, [collabEditProvider, onSaveAttempt, onSaveSuccess]);

	// Update polling interval callback with new poll() whenever it updates
	// Because setInterval callback does not track state changes (i.e. isSaving)
	useEffect(() => {
		if (pollIntervalId.current) {
			clearInterval(pollIntervalId.current);
			pollIntervalId.current = setInterval(poll, NCS_POLLING_INTERVAL);
		}
	}, [poll]);

	// Old save trigger logic that uses commit-status, which is innacurate due to being triggered on "ghost steps" (e.g. editor analytics steps)
	useEffect(() => {
		if (useEditorBodyChangeEvent || !collabEditProvider) {
			return;
		}

		const onCommitStatus = debounce(
			(payload: CommitStatusPayload) => {
				if (payload.status === 'attempt' && !pollIntervalId.current) {
					const id = setInterval(poll, NCS_POLLING_INTERVAL);
					pollIntervalId.current = id;
				}
			},
			COMMIT_STATUS_DEBOUNCE,
			{ leading: true, trailing: false },
		);

		collabEditProvider.on('commit-status', onCommitStatus);

		return () => {
			if (pollIntervalId.current) {
				clearInterval(pollIntervalId.current);
			}
			collabEditProvider.off('commit-status', onCommitStatus);
		}; // eslint-disable-next-line
	}, [collabEditProvider, useEditorBodyChangeEvent]);

	// New save trigger logic that uses lastOrganicBodyChangeAt, which excludes ghost steps
	const onDocumentChanged = useCallback(() => {
		if (pollIntervalId.current) {
			clearInterval(pollIntervalId.current);
		}

		const id = setInterval(poll, NCS_POLLING_INTERVAL);
		pollIntervalId.current = id;
	}, [poll]);

	const debouncedOnChange = useMemo(
		() => debounce(onDocumentChanged, COMMIT_STATUS_DEBOUNCE, { leading: true, trailing: false }),
		[onDocumentChanged],
	);

	useEffect(() => {
		if (!useEditorBodyChangeEvent) {
			return;
		}

		if (!!lastOrganicBodyChangeAt) {
			debouncedOnChange();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lastOrganicBodyChangeAt, useEditorBodyChangeEvent]);

	// Cancel any awaiting callbacks on unmount
	useEffect(() => {
		return () => {
			if (pollIntervalId.current) {
				clearInterval(pollIntervalId.current);
			}
			if (saveAttemptCallbackId.current) {
				cancelThrottle(saveAttemptCallbackId.current);
			}
			if (saveCompleteCallbackId.current) {
				cancelThrottle(saveCompleteCallbackId.current);
			}
		};
		// eslint-disable-next-line
	}, []);

	const saveIndicatorContextValue = useMemo(
		() => ({
			isSaving,
			savedTime,
			processNcsSaving: onSaveAttempt,
		}),
		[isSaving, savedTime, onSaveAttempt],
	);

	return (
		<SaveIndicatorContext.Provider value={saveIndicatorContextValue}>
			{children}
		</SaveIndicatorContext.Provider>
	);
};
